// src/pages/outsourcing_management/EnquiryPurchaseQuantities.tsx
//
// What the Inquiry's lines buy, as opposed to what the requisition lines
// asked for (EE-253 P3):
// - a raw-material line - round (Ø) or flat (W × H) - asks for lengths × mm.
//   It starts as 1 length of everything its requisition lines need; the
//   buyer can type another count and length (say 5 × 3000) and save them
//   together. Typing a length fills in the fewest lengths that hold every
//   piece (util/stockLengths); the buyer can still raise the count. The
//   server refuses a pair that does not hold every piece. A
//   rebuild of the line keeps them until they reset it, and shows the line
//   short if added requisition lines no longer fit.
// - a finishing line asks for a quantity in pcs, lot, m² or kg, with
//   fractions.
// Editable while the Inquiry is a Draft or Approved; read-only after.
import { FunctionComponent, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { useSetLinePurchase } from "hooks/useRequisitionPool";
import {
  InquiryDetailType,
  InquiryItemType,
  InquiryStatusEnum,
  InquiryTypeEnum,
  SERVICE_UNIT_LABELS,
  SERVICE_UNITS,
  ServiceUnitType,
} from "types/InquiryTypes";
import { longestPiece, minLengths } from "util/stockLengths";

type PropsType = {
  inquiry: InquiryDetailType;
  // The dialog's copy of the inquiry: patched optimistically, then replaced
  // by what the server answers.
  onReplace: (next: InquiryDetailType) => void;
};

const EDITABLE = new Set<string | null | undefined>([
  undefined,
  null,
  InquiryStatusEnum.DRAFT,
  InquiryStatusEnum.APPROVED,
]);

const lineName = (item: InquiryItemType) => item.product?.name ?? item.comment ?? `Line ${item.id}`;

/** A number field that only reports on blur / Enter, and only a change. */
const NumberCell: FunctionComponent<{
  value: number | null | undefined;
  disabled: boolean;
  step?: string;
  testId: string;
  label: string;
  onCommit: (value: number | null) => void;
}> = ({ value, disabled, step, testId, label, onCommit }) => {
  const [text, setText] = useState(value == null ? "" : String(value));
  useEffect(() => setText(value == null ? "" : String(value)), [value]);
  const commit = () => {
    const trimmed = text.trim().replace(",", ".");
    const next = trimmed === "" ? null : Number(trimmed);
    if (next !== null && !Number.isFinite(next)) {
      setText(value == null ? "" : String(value));
      return;
    }
    if (next !== (value ?? null)) onCommit(next);
  };
  return (
    <TextField
      size="small"
      type="number"
      label={label}
      value={text}
      disabled={disabled}
      inputProps={{ step: step ?? "1", min: 0, "data-testid": testId }}
      sx={{ width: 130 }}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
};

// Plain numbers, as in the inputs and the server's messages.
const mm = (value: number) => `${value} mm`;

/** Lengths × length of one raw-material line, saved together: either alone
 * could pass through a pair that is short. */
const StockQuantity: FunctionComponent<{
  item: InquiryItemType;
  disabled: boolean;
  onSave: (body: { quantity: number; stockLengthMm: number }) => void;
}> = ({ item, disabled, onSave }) => {
  const [count, setCount] = useState(String(item.quantity ?? ""));
  const [length, setLength] = useState(String(item.stockLengthMm ?? ""));
  useEffect(() => {
    setCount(String(item.quantity ?? ""));
    setLength(String(item.stockLengthMm ?? ""));
  }, [item.quantity, item.stockLengthMm]);
  const nextCount = Number(count.trim());
  const nextLength = Number(length.trim().replace(",", "."));
  const longest = longestPiece(item.cuts ?? []);
  const tooLong = nextLength > 0 && longest != null && longest > nextLength;
  const valid =
    Number.isInteger(nextCount) &&
    nextCount >= 1 &&
    Number.isFinite(nextLength) &&
    nextLength > 0 &&
    !tooLong;
  // A new length recounts the lengths: the fewest that hold every piece.
  const changeLength = (value: string) => {
    setLength(value);
    const fewest = minLengths(item.cuts ?? [], Number(value.trim().replace(",", ".")));
    if (fewest != null) setCount(String(fewest));
  };
  const dirty = nextCount !== item.quantity || nextLength !== item.stockLengthMm;
  const save = () => valid && dirty && onSave({ quantity: nextCount, stockLengthMm: nextLength });
  const field = (label: string, value: string, set: (v: string) => void, testId: string, step: string) => (
    <TextField
      size="small"
      type="number"
      label={label}
      value={value}
      disabled={disabled}
      error={value !== "" && !valid}
      inputProps={{ step, min: 0, "data-testid": testId }}
      sx={{ width: 130 }}
      onChange={(e) => set(e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && save()}
    />
  );
  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {field("Lengths", count, setCount, `purchase-lengths-${item.id}`, "1")}
        <Typography variant="body2">×</Typography>
        {field("Length (mm)", length, changeLength, `purchase-stock-length-${item.id}`, "0.01")}
        <Typography variant="body2">mm lengths</Typography>
        {!disabled && dirty && (
          <Button
            size="small"
            variant="contained"
            disabled={!valid}
            onClick={save}
            data-testid={`purchase-save-${item.id}`}
          >
            Save
          </Button>
        )}
      </Box>
      {item.neededMm != null && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Needed: {mm(item.neededMm)}
        </Typography>
      )}
      {tooLong && (
        <Typography variant="caption" color="error" display="block" data-testid={`purchase-too-long-${item.id}`}>
          A {mm(longest ?? 0)} piece does not fit in a {mm(nextLength)} length
        </Typography>
      )}
      {item.shortMm != null && (
        <Typography variant="caption" color="error" display="block" data-testid={`purchase-short-${item.id}`}>
          {item.quantity} × {mm(item.stockLengthMm ?? 0)} is {mm(item.shortMm)} short - the added
          requisition lines no longer fit
        </Typography>
      )}
    </Box>
  );
};

const EnquiryPurchaseQuantities: FunctionComponent<PropsType> = ({ inquiry, onReplace }) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const mutation = useSetLinePurchase(token, inquiry, onReplace);
  const editable = EDITABLE.has(inquiry.state);
  const finishing = inquiry.type === InquiryTypeEnum.FINISH;

  const stockLines = inquiry.items.filter((item) => item.stockKey);
  const serviceLines = finishing ? inquiry.items : [];
  if (!stockLines.length && !serviceLines.length) return null;

  const save = (item: InquiryItemType, body: Parameters<typeof mutation.mutate>[0]["body"]) =>
    mutation.mutate(
      { itemId: item.id, body },
      {
        onError: (err: any) =>
          addToast({ type: "error", message: String(err?.message ?? err), keep: true }),
      }
    );

  return (
    <Box sx={{ mt: 2 }} data-testid="enquiry-purchase-quantities">
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        What the supplier is asked for
      </Typography>
      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {["Line", "Stock / service", "Quantity", ""].map((h, i) => (
                <TableCell key={i} sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {stockLines.map((item) => (
              <TableRow key={item.id} data-testid={`purchase-stock-${item.id}`}>
                <TableCell>{inquiry.items.indexOf(item) + 1}</TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <Typography variant="body2">{lineName(item)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.comment}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <StockQuantity
                    item={item}
                    disabled={!editable || mutation.isLoading}
                    onSave={(body) => save(item, body)}
                  />
                </TableCell>
                <TableCell>
                  {editable && item.quantityOverridden && (
                    <Button
                      size="small"
                      disabled={mutation.isLoading}
                      onClick={() => save(item, { quantity: null })}
                      data-testid={`purchase-reset-${item.id}`}
                    >
                      Reset to 1 × total
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {serviceLines.map((item) => (
              <TableRow key={item.id} data-testid={`purchase-service-${item.id}`}>
                <TableCell>{inquiry.items.indexOf(item) + 1}</TableCell>
                <TableCell>{lineName(item)}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <NumberCell
                      label="Quantity"
                      value={item.serviceQuantity ?? null}
                      disabled={!editable}
                      step="0.001"
                      testId={`purchase-service-qty-${item.id}`}
                      onCommit={(value) => {
                        if (value !== null && value <= 0) {
                          addToast({ type: "error", message: "The quantity must be more than 0" });
                          return;
                        }
                        save(item, { serviceQuantity: value });
                      }}
                    />
                    <TextField
                      select
                      size="small"
                      label="Unit"
                      value={item.serviceUnit ?? ""}
                      disabled={!editable}
                      sx={{ width: 100 }}
                      inputProps={{ "data-testid": `purchase-service-unit-${item.id}` }}
                      onChange={(e) =>
                        save(item, { serviceUnit: (e.target.value || null) as ServiceUnitType | null })
                      }
                    >
                      <MenuItem value="">-</MenuItem>
                      {SERVICE_UNITS.map((unit) => (
                        <MenuItem key={unit} value={unit}>
                          {SERVICE_UNIT_LABELS[unit]}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </TableCell>
                <TableCell />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default EnquiryPurchaseQuantities;
